import React, { useState, useEffect } from 'react';

export default function LineItemSell({ it, updateItem, hideDeposit = false, readOnly = false }) {
  const trade = Number(it.tradePrice) || 0;
  const tradeShipping = Number(it.shipping) || 0;
  const tradeTariff = Number(it.tariff) || 0;

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const fmt2 = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : '';
  };

  /* ---------------------------------------------------
     LOCAL DISPLAY STATE
  --------------------------------------------------- */
  const [sellInput, setSellInput] = useState('');
  const [markupInput, setMarkupInput] = useState('');
  const [clientShippingDisplay, setClientShippingDisplay] = useState('');
  const [shippingMarkupInput, setShippingMarkupInput] = useState('');
  const [clientTariffDisplay, setClientTariffDisplay] = useState('');
  const [depositInput, setDepositInput] = useState('');
  const [msrpInput, setMsrpInput] = useState('');

  /* ---------------------------------------------------
     SYNC DISPLAY FROM DATA
  --------------------------------------------------- */
  useEffect(() => {
    /* ---------- ITEM SELL PRICE ---------- */
    if (it.lastEdited !== 'sell') {
      const computedSell =
        it.markup?.type === '%'
          ? trade + trade * (Number(it.markup.value) / 100)
          : trade + Number(it.markup.value || 0);

      setSellInput(fmt2(computedSell));
    }

    if (it.lastEdited !== 'markup') {
      setMarkupInput(fmt2(it.markup?.value || 0));
    }

    /* ---------- CUSTOMER SHIPPING ---------- */
    if (it.shippingLastEdited !== 'clientShipping') {
      const shipMarkupValue = Number(it.shippingMarkup?.value || 0);

      const clientShipping =
        it.shippingMarkup?.type === '%'
          ? tradeShipping + tradeShipping * (shipMarkupValue / 100)
          : tradeShipping + shipMarkupValue;

      setClientShippingDisplay(fmt2(clientShipping));
    }

    /* ---------- CUSTOMER TARIFF (mirrors Trade Tariff) ---------- */
    setClientTariffDisplay(fmt2(tradeTariff));
  }, [trade, tradeShipping, tradeTariff, it.markup, it.shippingMarkup, it.lastEdited, it.shippingLastEdited]);

  useEffect(() => {
    const shippingMarkupValue = Number(it.shippingMarkup?.value || 0);
    setShippingMarkupInput(shippingMarkupValue === 0 ? '' : fmt2(shippingMarkupValue));
  }, [it.shippingMarkup?.value]);

  useEffect(() => {
    const depositValue = Number(it.deposit || 0);
    setDepositInput(depositValue === 0 ? '' : fmt2(depositValue));
  }, [it.deposit]);

  useEffect(() => {
    const msrpValue = Number(it.msrp || 0);
    setMsrpInput(msrpValue === 0 ? '' : fmt2(msrpValue));
  }, [it.msrp]);

  /* ---------------------------------------------------
     SELLING PRICE HANDLERS
  --------------------------------------------------- */
  const handleSellingChange = (val) => {
    setSellInput(val);
    updateItem(it.itemId, { lastEdited: 'sell' });
  };

  const handleSellingBlur = () => {
    const clean = toNum(sellInput);
    if (clean == null) return;
    const safeSell = Math.max(clean, 0);

    const newMarkup =
      it.markup?.type === '%'
        ? trade > 0
          ? ((safeSell - trade) / trade) * 100
          : 0
        : safeSell - trade;

    setSellInput(fmt2(safeSell));
    setMarkupInput(fmt2(newMarkup));

    updateItem(it.itemId, {
      clientUnit: safeSell,
      markup: {
        type: it.markup.type,
        value: newMarkup,
      },
      lastEdited: 'sell',
    });
  };

  /* ---------------------------------------------------
     MARKUP HANDLERS
  --------------------------------------------------- */
  const handleMarkupChange = (val) => {
    setMarkupInput(val);
    updateItem(it.itemId, { lastEdited: 'markup' });
  };

  const handleMarkupBlur = () => {
    const m = toNum(markupInput);
    if (m == null) return;

    // % markup on a $0 trade price always produces $0 — switch to fixed so the
    // entered value is treated as a flat dollar markup instead.
    if (it.markup?.type === '%' && trade === 0 && m !== 0) {
      const newSell = Math.max(m, 0);
      setSellInput(fmt2(newSell));
      updateItem(it.itemId, {
        markup: { type: 'fixed', value: m },
        clientUnit: newSell,
        lastEdited: 'markup',
      });
      return;
    }

    const safeMarkup = it.markup?.type === '%' ? Math.max(m, -100) : m;

    const newSellRaw =
      it.markup?.type === '%' ? trade + trade * (safeMarkup / 100) : trade + safeMarkup;
    // For fixed type, clamp the stored markup value to prevent it going below -trade
    // (which would produce a negative sell price that gets floored to $0 while
    // markup.value stays inconsistently negative).
    const clampedMarkup =
      it.markup?.type === 'fixed' ? Math.max(safeMarkup, -trade) : safeMarkup;
    const newSell = Math.max(newSellRaw, 0);

    setMarkupInput(fmt2(clampedMarkup));
    setSellInput(fmt2(newSell));

    updateItem(it.itemId, {
      markup: { ...it.markup, value: clampedMarkup },
      clientUnit: newSell,
      lastEdited: 'markup',
    });
  };

  return (
    <div className="li-col li-sell">
      {/* Selling Price */}
      <label>Selling Price</label>
      <div className="if-currency">
        <span className="if-currency-prefix">$</span>
        <input
          className="if-input"
          type="number"
          step="0.01"
          value={sellInput}
          disabled={readOnly}
          onChange={(e) => handleSellingChange(e.target.value)}
          onFocus={(e) => {
            if (e.target.value === '0' || e.target.value === '0.00') {
              setSellInput('');
            }
          }}
          onBlur={handleSellingBlur}
        />
      </div>

      {/* Item Markup */}
      <label>Item Markup</label>
      <div className="inline-inputs">
        <select
          className="if-select"
          value={it.markup?.type}
          disabled={readOnly}
          onChange={(e) =>
            updateItem(it.itemId, {
              markup: { ...it.markup, type: e.target.value },
              lastEdited: 'markup',
            })
          }
        >
          <option value="%">%</option>
          <option value="fixed">Fixed</option>
        </select>

        <div className={it.markup?.type === 'fixed' ? 'if-currency' : ''}>
          {it.markup?.type === 'fixed' && <span className="if-currency-prefix">$</span>}
          <input
            className="if-input"
            type="number"
            step="0.01"
            value={markupInput}
            disabled={readOnly}
            onChange={(e) => handleMarkupChange(e.target.value)}
            onFocus={(e) => {
              if (e.target.value === '0' || e.target.value === '0.00') {
                setMarkupInput('');
              }
            }}
            onBlur={handleMarkupBlur}
          />
        </div>
      </div>

      {/* Shipping Markup */}
      <label>Shipping Markup</label>
      <div className="inline-inputs">
        <select
          className="if-select"
          value={it.shippingMarkup?.type}
          disabled={readOnly}
          onChange={(e) =>
            updateItem(it.itemId, {
              shippingMarkup: {
                ...it.shippingMarkup,
                type: e.target.value,
              },
            })
          }
        >
          <option value="%">%</option>
          <option value="fixed">Fixed</option>
        </select>

        <div className={it.shippingMarkup?.type === 'fixed' ? 'if-currency' : ''}>
          {it.shippingMarkup?.type === 'fixed' && <span className="if-currency-prefix">$</span>}
          <input
            className="if-input"
            type="number"
            step="0.01"
            value={shippingMarkupInput}
            disabled={readOnly}
            onChange={(e) => setShippingMarkupInput(e.target.value)}
            onFocus={(e) => {
              if (e.target.value === '0' || e.target.value === '0.00') {
                setShippingMarkupInput('');
              }
            }}
            onBlur={() => {
              const value = toNum(shippingMarkupInput) ?? 0;
              setShippingMarkupInput(value === 0 ? '' : fmt2(value));
              updateItem(it.itemId, {
                shippingMarkup: {
                  ...it.shippingMarkup,
                  value,
                },
                shippingLastEdited: 'shippingMarkup',
              });
            }}
          />
        </div>
      </div>

      {/* MSRP (client-facing reference price) */}
      <label>MSRP</label>
      <div className="if-currency">
        <span className="if-currency-prefix">$</span>
        <input
          className="if-input"
          type="number"
          step="0.01"
          value={msrpInput}
          disabled={readOnly}
          onChange={(e) => setMsrpInput(e.target.value)}
          onFocus={(e) => {
            if (e.target.value === '0' || e.target.value === '0.00') {
              setMsrpInput('');
            }
          }}
          onBlur={() => updateItem(it.itemId, { msrp: toNum(msrpInput) ?? 0 })}
        />
      </div>

      {/* Customer Shipping */}
      <label>Customer Shipping</label>
      <div className="if-currency">
        <span className="if-currency-prefix">$</span>
        <input
          className="if-input"
          type="number"
          step="0.01"
          value={clientShippingDisplay}
          disabled={readOnly}
          onChange={(e) => setClientShippingDisplay(e.target.value)}
          onFocus={(e) => {
            if (e.target.value === '0' || e.target.value === '0.00') {
              setClientShippingDisplay('');
            }
          }}
          onBlur={() => {
            const clean = toNum(clientShippingDisplay);
            if (clean == null) return;
            const safeVal = Math.max(clean, 0);

            const newMarkupValue =
              it.shippingMarkup?.type === '%'
                ? tradeShipping > 0
                  ? ((safeVal - tradeShipping) / tradeShipping) * 100
                  : 0
                : safeVal - tradeShipping;

            setClientShippingDisplay(fmt2(safeVal));
            setShippingMarkupInput(fmt2(newMarkupValue));

            updateItem(it.itemId, {
              shippingMarkup: { ...it.shippingMarkup, value: newMarkupValue },
              shippingLastEdited: 'clientShipping',
            });
          }}
        />
      </div>

      {/* Customer Tariff (mirrors Trade Tariff — no markup) */}
      <label>Customer Tariff</label>
      <div className="if-currency">
        <span className="if-currency-prefix">$</span>
        <input
          className="if-input"
          type="number"
          step="0.01"
          value={clientTariffDisplay}
          disabled={readOnly}
          onChange={(e) => setClientTariffDisplay(e.target.value)}
          onFocus={(e) => {
            if (e.target.value === '0' || e.target.value === '0.00') {
              setClientTariffDisplay('');
            }
          }}
          onBlur={() => updateItem(it.itemId, { tariff: toNum(clientTariffDisplay) ?? 0 })}
        />
      </div>

      {!hideDeposit && (
        <>
          <label>Deposit %</label>
          <input
            className="if-input"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={depositInput}
            onChange={(e) => setDepositInput(e.target.value)}
            onFocus={(e) => {
              if (e.target.value === '0' || e.target.value === '0.00') {
                setDepositInput('');
              }
            }}
            onBlur={() => {
              const raw = toNum(depositInput);
              const safe = raw == null ? 0 : Math.max(0, Math.min(100, raw));
              setDepositInput(safe === 0 ? '' : fmt2(safe));
              updateItem(it.itemId, { deposit: safe });
            }}
          />
        </>
      )}
    </div>
  );
}
